import { DestroyRef, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import {
  canEnterVideoFullscreen,
  canRequestElementFullscreen,
  enterVideoFullscreen,
  exitElementFullscreen,
  fullscreenChanges,
  getFullscreenElement,
  hasStandardElementFullscreen,
  isIphoneClassDevice,
  requestElementFullscreen,
  videoFullscreenChanges
} from './fullscreen.utils';

export interface FullscreenToggleOptions {
  /** Element promoted to fullscreen where the browser supports element fullscreen. */
  target: () => Element | null | undefined;
  /** Video handed to the native player where it does not (iPhone). */
  fallbackVideo?: () => HTMLVideoElement | null | undefined;
  /** Receives every state update, e.g. to mirror it and mark for check (zoneless). */
  onChange?: (isFullscreen: boolean) => void;
  doc?: Document;
  /**
   * Skip element fullscreen and use only the native video player. Defaults to
   * true on iPhone-class devices, where element fullscreen never works (KAN-635).
   */
  nativeVideoOnly?: boolean;
}

/**
 * One fullscreen button's behavior (KAN-620). Uses element fullscreen on the
 * target when the browser has it (desktop, Android, iPad), otherwise the
 * native iOS video player. `isFullscreen` only reports what the browser
 * confirms through its fullscreen events, so a button can never stick on
 * "exit fullscreen" while nothing is fullscreen.
 *
 * KAN-635: an iPhone goes straight to the native video player, inside the
 * tap, even if it exposes an element API it then refuses; and on any device a
 * refused element request falls back to the native video player.
 */
export class FullscreenToggle {
  isFullscreen = false;
  private nativeVideoActive = false;
  private readonly doc: Document;
  private readonly nativeVideoOnly: boolean;
  private readonly subscriptions = new Subscription();
  private watchedVideo: HTMLVideoElement | null = null;
  private videoSubscription: Subscription | null = null;

  constructor(private readonly options: FullscreenToggleOptions) {
    this.doc = options.doc ?? document;
    this.nativeVideoOnly = options.nativeVideoOnly ?? isIphoneClassDevice();
    this.subscriptions.add(fullscreenChanges(this.doc).subscribe(() => this.sync()));
  }

  /** Whether toggle() can do anything real here. Hide the button when false. */
  get isAvailable(): boolean {
    const target = this.options.target() ?? this.doc.documentElement;
    return this.canUseElementFullscreen(target) || canEnterVideoFullscreen(this.options.fallbackVideo?.());
  }

  toggle(): void {
    if (getFullscreenElement(this.doc)) {
      this.set(false);
      exitElementFullscreen(this.doc).catch(() => this.sync());
      return;
    }
    const target = this.options.target();
    if (target && this.canUseElementFullscreen(target)) {
      // The standard API is reflected immediately (desktop behavior before
      // KAN-620); a rejection or fullscreenchange reconciles it. The prefixed
      // call reports nothing, so that path waits for webkitfullscreenchange.
      if (hasStandardElementFullscreen(target)) this.set(true);
      requestElementFullscreen(target).catch(() => {
        this.enterNativeVideo();
        this.sync();
      });
      return;
    }
    this.enterNativeVideo();
  }

  /** Re-reads the browser's fullscreen state. */
  sync(): void {
    const element = getFullscreenElement(this.doc);
    const target = this.options.target();
    this.set(this.nativeVideoActive || (!!element && element === target));
  }

  destroy(): void {
    this.subscriptions.unsubscribe();
    this.videoSubscription?.unsubscribe();
  }

  private canUseElementFullscreen(target: Element): boolean {
    return !this.nativeVideoOnly && canRequestElementFullscreen(target, this.doc);
  }

  private enterNativeVideo(): void {
    const video = this.options.fallbackVideo?.();
    if (!canEnterVideoFullscreen(video)) return;
    this.watchVideo(video);
    enterVideoFullscreen(video);
  }

  private watchVideo(video: HTMLVideoElement): void {
    if (this.watchedVideo === video) return;
    this.videoSubscription?.unsubscribe();
    this.watchedVideo = video;
    this.nativeVideoActive = false;
    this.videoSubscription = videoFullscreenChanges(video).subscribe(active => {
      this.nativeVideoActive = active;
      this.sync();
    });
  }

  private set(isFullscreen: boolean): void {
    this.isFullscreen = isFullscreen;
    this.options.onChange?.(isFullscreen);
  }
}

/** Creates a FullscreenToggle that is torn down with the calling component. */
export function injectFullscreenToggle(options: FullscreenToggleOptions): FullscreenToggle {
  const toggle = new FullscreenToggle(options);
  inject(DestroyRef).onDestroy(() => toggle.destroy());
  return toggle;
}
